import { AlertTriangle, Check, ChevronsUpDown, Plus } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useDuplicateDetection } from '@/components/matter-form/useDuplicateDetection';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { cn } from '@/lib/utils';
import { useGetFirmsQuery } from '@/services/api/firmsService';
import { useCreateIntakeMutation } from '@/services/api/intakeService';
import { CreateIntakePayload } from '@/services/types/intake-types';
import { selectUser } from '@/state/selectors/root-selectors';

import { Button } from '../../ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { DuplicateWarningPanel } from '../components/DuplicateWarningPanel';
import { ACCIDENT_TYPES } from '../constants';
import { isScreeningSheetEnabled } from '../screening';

interface CreateIntakeFormData {
  clientName: string;
  firmId: string;
  firmName: string;
  caseType: string;
}

const CreateIntakePage: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const [formData, setFormData] = useState<CreateIntakeFormData>({
    clientName: '',
    firmId: '',
    firmName: '',
    caseType: '',
  });
  const [firmOpen, setFirmOpen] = useState(false);
  const [caseTypeOpen, setCaseTypeOpen] = useState(false);

  const { data: firms, isLoading: firmsLoading } = useGetFirmsQuery({});
  const [createIntake, { isLoading: isCreating }] = useCreateIntakeMutation();

  // Duplicate detection
  const { duplicates } = useDuplicateDetection(formData.clientName);
  const debouncedName = useDebouncedValue(formData.clientName.trim(), 500);
  const [dismissed, setDismissed] = useState(false);
  const prevDebouncedName = useRef(debouncedName);

  useEffect(() => {
    if (debouncedName !== prevDebouncedName.current) {
      prevDebouncedName.current = debouncedName;
      setDismissed(false);
    }
  }, [debouncedName]);

  const showWarning = duplicates.length > 0 && !dismissed;

  // Check if user should see the screening sheet
  const showScreeningSheet = isScreeningSheetEnabled(user?.email);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const firmOptions = firms?.map((firm: any) => ({
    id: firm.id,
    name: firm.name,
  })) || [];

  const isFormValid = () => {
    return formData.clientName.trim() && formData.firmId;
  };

  const handleCreateIntake = async () => {
    if (!user) {
      toast.error('User must be logged in to create an intake');
      return;
    }

    if (!isFormValid()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const intakePayload: CreateIntakePayload = {
        firm: formData.firmId,
        creator: user.id,
        personal_info: {
          full_name: formData.clientName,
        },
        ...(formData.caseType && {
          incident_info: {
            type: formData.caseType,
          },
        }),
      };

      const result = await createIntake(intakePayload).unwrap();

      // Navigate to the intake page (with screening if enabled)
      if (showScreeningSheet) {
        navigate(`/intakes/${result.id}?screening=true`);
      } else {
        navigate(`/intakes/${result.id}`);
      }
    } catch {
      toast.error('Failed to create intake. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Form Card */}
        <div className="bg-white rounded-[24px] shadow-sm border border-zinc-200/80 overflow-hidden">
          {/* Card Header */}
          <div className="px-6 pt-6 pb-4">
            <h1 className="text-xl font-black text-zinc-900 tracking-tight">
              New Intake
            </h1>
          </div>

          {/* Form Fields */}
          <div className="px-6 pb-4 space-y-4">
            {/* Client Name */}
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="block space-y-2">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                Client Name
                <span className="text-rose-500">*</span>
              </span>
              <input
                type="text"
                value={formData.clientName}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, clientName: e.target.value }))
                }
                placeholder="Enter full name"
                className={cn(
                  'w-full h-12 rounded-xl px-4 text-sm font-semibold',
                  'bg-zinc-100 text-zinc-900 placeholder:text-zinc-400',
                  'border-2 border-transparent',
                  'focus:outline-none focus:border-zinc-900 focus:bg-white',
                  'transition-all duration-200'
                )}
              />
            </label>

            {/* Duplicate Warning */}
            {showWarning && (
              <DuplicateWarningPanel
                duplicates={duplicates}
                onDismiss={() => setDismissed(true)}
              />
            )}

            {/* Firm Selection */}
            <div className="space-y-2">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                Firm
                <span className="text-rose-500">*</span>
              </span>
              <Popover open={firmOpen} onOpenChange={setFirmOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={firmOpen}
                    aria-label="Select a firm"
                    disabled={firmsLoading}
                    className={cn(
                      'w-full h-12 justify-between rounded-xl px-4 text-sm font-semibold',
                      'bg-zinc-100 border-2 border-transparent',
                      'hover:bg-zinc-100 hover:border-zinc-300',
                      'focus:border-zinc-900 focus:bg-white',
                      'transition-all duration-200',
                      !formData.firmId && 'text-zinc-400'
                    )}
                  >
                    {firmsLoading
                      ? 'Loading firms...'
                      : formData.firmName || 'Select a firm'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-zinc-400" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search firms..." className="h-10" />
                    <CommandList>
                      <CommandEmpty>No firm found.</CommandEmpty>
                      <CommandGroup>
                        {firmOptions.map((firm: { id: string; name: string }) => (
                          <CommandItem
                            key={firm.id}
                            value={firm.name}
                            onSelect={() => {
                              setFormData((prev) => ({
                                ...prev,
                                firmId: firm.id,
                                firmName: firm.name,
                              }));
                              setFirmOpen(false);
                            }}
                            className="text-sm"
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                formData.firmId === firm.id ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            {firm.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Case Type Selection */}
            <div className="space-y-2">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-500">
                Case Type
              </span>
              <Popover open={caseTypeOpen} onOpenChange={setCaseTypeOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={caseTypeOpen}
                    aria-label="Select a case type"
                    className={cn(
                      'w-full h-12 justify-between rounded-xl px-4 text-sm font-semibold',
                      'bg-zinc-100 border-2 border-transparent',
                      'hover:bg-zinc-100 hover:border-zinc-300',
                      'focus:border-zinc-900 focus:bg-white',
                      'transition-all duration-200',
                      !formData.caseType && 'text-zinc-400'
                    )}
                  >
                    {formData.caseType || 'Select case type (optional)'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-zinc-400" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search case types..." className="h-10" />
                    <CommandList>
                      <CommandEmpty>No case type found.</CommandEmpty>
                      <CommandGroup>
                        {ACCIDENT_TYPES.map((type) => (
                          <CommandItem
                            key={type}
                            value={type}
                            onSelect={() => {
                              setFormData((prev) => ({ ...prev, caseType: type }));
                              setCaseTypeOpen(false);
                            }}
                            className="text-sm"
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                formData.caseType === type ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            {type}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

          </div>

          {/* Action Buttons */}
          <div className="px-6 pb-6 pt-2 space-y-3">
            <button
              type="button"
              onClick={handleCreateIntake}
              disabled={!isFormValid() || isCreating}
              className={cn(
                'w-full h-14 rounded-2xl text-base font-black transition-all duration-200',
                'flex items-center justify-center gap-2',
                isFormValid() && !isCreating
                  ? showWarning
                    ? 'bg-amber-600 text-white hover:bg-amber-700 active:scale-[0.98]'
                    : 'bg-zinc-900 text-white hover:bg-zinc-800 active:scale-[0.98]'
                  : 'bg-zinc-200 text-zinc-400 cursor-not-allowed'
              )}
            >
              {isCreating ? (
                <>
                  <div className="w-5 h-5 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
                  Creating...
                </>
              ) : showWarning ? (
                <>
                  <AlertTriangle className="w-5 h-5" />
                  Create Anyway
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  Create Intake
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                const intakesSearch = sessionStorage.getItem('intakesSearch') || '';
                navigate(`/intakes${intakesSearch}`);
              }}
              disabled={isCreating}
              className={cn(
                'w-full h-12 rounded-xl text-sm font-bold transition-all duration-200',
                'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Hint Text */}
        <p className="text-center text-[11px] text-zinc-400 mt-6">
          You can add more details after creating the intake
        </p>
      </div>
    </div>
  );
};

export default CreateIntakePage;
